import {
  RECALL_TICKS,
  TICK_RATE,
  abilityCharges,
  abilityCooldown,
  abilityCooldownScale,
  abilityHasteMultiplier,
  canRankUp,
  getHeroDef,
  heroStats,
  isHeroUnit,
  isStructureUnit,
  MINION_STATS,
  SHOP,
  STRUCTURE_STATS,
  xpToNext,
  MAX_LEVEL,
  type AbilitySlot,
  type HeroId,
  type HeroState,
  type Simulation,
  type Team,
} from '@moba/shared';
import { itemIcon } from './Shop.ts';
import { KIT_DESCRIPTIONS, KIT_NAMES, abilityIcon } from './abilityIcons.ts';
import { abilityTooltipData } from './abilityTooltip.ts';

import { t, heroDisplayName } from './i18n.ts';
type HudSlot = 'attack' | AbilitySlot;

const SLOT_ORDER: HudSlot[] = ['attack', 'q', 'w', 'e', 'r'];

interface SlotElement {
  root: HTMLElement;
  fill: HTMLElement;
  text: HTMLElement;
  name: HTMLElement;
  icon: HTMLImageElement;
  pips?: HTMLElement;
  plus?: HTMLButtonElement;
  /** cargas restantes (flechas de Link) y barra de la recarga en curso */
  charges?: HTMLElement;
  chargeBar?: HTMLElement;
}

export class Hud {
  private readonly playerName = document.getElementById('player-name') as HTMLElement;
  private readonly hpText = document.getElementById('player-hp-text') as HTMLElement;
  private readonly hpFill = document.getElementById('hp-fill') as HTMLElement;
  private readonly gold = document.getElementById('player-gold') as HTMLElement | null;
  private readonly statValues = new Map<string, HTMLElement>();
  private readonly inventorySlots: HTMLElement[] = [];
  private readonly heroBoard = document.getElementById('hero-board') as HTMLElement | null;
  private readonly boardTeams = document.getElementById('board-teams') as HTMLElement | null;
  private readonly boardScore = document.getElementById('board-score') as HTMLElement | null;
  private boardOpen = false;
  private boardSignature = '';
  private readonly recallBar = document.getElementById('recall-bar') as HTMLElement | null;
  private readonly recallFill = document.getElementById('recall-fill') as HTMLElement | null;
  private readonly recallText = document.getElementById('recall-text') as HTMLElement | null;
  private readonly portrait = document.getElementById('portrait') as HTMLElement;
  private readonly timer = document.getElementById('timer') as HTMLElement;
  private readonly modeTag = document.getElementById('mode-tag') as HTMLElement;
  private readonly scoreboard = document.getElementById('scoreboard') as HTMLElement;
  private readonly scoreBlue = document.getElementById('score-blue') as HTMLElement;
  private readonly scoreRed = document.getElementById('score-red') as HTMLElement;
  private readonly cameraTag = document.getElementById('camera-tag') as HTMLElement | null;
  private readonly killfeed = document.getElementById('killfeed') as HTMLElement;
  private readonly matchEnd = document.getElementById('match-end') as HTMLElement;
  private readonly endTitle = document.getElementById('end-title') as HTMLElement;
  private readonly endScore = document.getElementById('end-score') as HTMLElement;
  private readonly fpsLabel = document.getElementById('fps') as HTMLElement;
  private readonly netStatus = document.getElementById('net-status') as HTMLElement | null;
  private readonly pingEl = document.getElementById('ping') as HTMLElement | null;
  private lastPingText = '';
  /** el canvas del juego lleva el cursor nativo; `aim-hot` lo pone rojo sobre un enemigo */
  private readonly gameCanvas = document.getElementById('game') as HTMLElement;
  private aimHot = false;
  private readonly debug = document.getElementById('debug') as HTMLElement;

  private readonly slots = new Map<HudSlot, SlotElement>();
  private readonly lastCooldown = new Map<HudSlot, number>();
  private readonly abilityTooltip = document.getElementById('ability-tooltip') as HTMLElement | null;
  private tooltipSlot: HudSlot | null = null;
  private tooltipAnchor: HTMLElement | null = null;
  private tooltipSignature = '';
  private lastPlayer: HeroState | null = null;
  private heroId: HeroId | null = null;
  private readonly levelBadge: HTMLElement;
  private readonly xpFill: HTMLElement;
  /** se llama al pulsar el "+" de una habilidad */
  onRankUp: (slot: AbilitySlot) => void = () => {};

  constructor() {
    for (const slot of SLOT_ORDER) {
      const root = document.querySelector<HTMLElement>(`.slot[data-slot="${slot}"]`);
      if (!root) continue;
      // ilustración de la habilidad, detrás de la tecla, el nombre y el cooldown
      const icon = document.createElement('img');
      icon.className = 'icon';
      icon.alt = '';
      icon.draggable = false;
      root.insertBefore(icon, root.firstChild);
      const element: SlotElement = {
        root,
        icon,
        fill: root.querySelector<HTMLElement>('.cd-fill') as HTMLElement,
        text: root.querySelector<HTMLElement>('.cd-text') as HTMLElement,
        name: root.querySelector<HTMLElement>('.name') as HTMLElement,
      };
      if (slot !== 'attack') {
        const pips = document.createElement('span');
        pips.className = 'pips';
        root.appendChild(pips);
        const plus = document.createElement('button');
        plus.className = 'rank-up';
        plus.textContent = '+';
        plus.title = t('Subir habilidad (Shift+{key})', { key: slot.toUpperCase() });
        plus.hidden = true;
        plus.addEventListener('mousedown', (event) => {
          event.stopPropagation();
          event.preventDefault();
          this.onRankUp(slot);
        });
        root.parentElement?.insertBefore(plus, root);
        element.pips = pips;
        element.plus = plus;
        const charges = document.createElement('span');
        charges.className = 'charges';
        charges.hidden = true;
        const chargeBar = document.createElement('span');
        chargeBar.className = 'charge-bar';
        chargeBar.hidden = true;
        chargeBar.appendChild(document.createElement('i'));
        root.append(charges, chargeBar);
        element.charges = charges;
        element.chargeBar = chargeBar;
      }
      this.slots.set(slot, element);
      root.addEventListener('mouseenter', () => this.showAbilityTooltip(slot, root));
      root.addEventListener('mouseleave', () => this.hideAbilityTooltip());
    }

    this.levelBadge = document.createElement('span');
    this.levelBadge.id = 'level-badge';
    this.portrait.appendChild(this.levelBadge);
    const xpTrack = document.createElement('div');
    xpTrack.id = 'xp-track';
    this.xpFill = document.createElement('div');
    this.xpFill.id = 'xp-fill';
    xpTrack.appendChild(this.xpFill);
    document.getElementById('hp-track')?.after(xpTrack);
    document.getElementById('board-close')?.addEventListener('click', () => this.setHeroBoardOpen(false));
  }

  private portraits = new Map<HeroId, string>();
  private portraitHero = '';

  /** Retratos de frente (busto del modelo) generados al cargar; sustituyen a la inicial. */
  setPortraits(portraits: Map<HeroId, string>): void {
    this.portraits = portraits;
    this.portraitHero = '';
  }

  get isHeroBoardOpen(): boolean {
    return this.boardOpen;
  }

  setHeroBoardOpen(open: boolean): void {
    this.boardOpen = open;
    if (this.heroBoard) this.heroBoard.hidden = !open;
    if (open) this.boardSignature = '';
  }

  toggleHeroBoard(): void {
    this.setHeroBoardOpen(!this.boardOpen);
  }

  setHero(heroId: HeroId): void {
    if (this.heroId === heroId) return;
    this.heroId = heroId;
    const names = KIT_NAMES[heroId];
    for (const slot of SLOT_ORDER) {
      const element = this.slots.get(slot);
      if (!element) continue;
      element.name.textContent = names[slot];
      element.icon.src = abilityIcon(heroId, slot);
      const description = KIT_DESCRIPTIONS[heroId][slot];
      element.root.removeAttribute('title');
      element.root.setAttribute('aria-label', description ? `${names[slot]}: ${description}` : names[slot]);
    }
  }

  private showAbilityTooltip(slot: HudSlot, anchor: HTMLElement): void {
    this.tooltipSlot = slot;
    this.tooltipAnchor = anchor;
    this.tooltipSignature = '';
    if (this.abilityTooltip) {
      this.abilityTooltip.hidden = false;
      this.renderAbilityTooltip();
    }
  }

  private hideAbilityTooltip(): void {
    this.tooltipSlot = null;
    this.tooltipAnchor = null;
    this.tooltipSignature = '';
    if (this.abilityTooltip) this.abilityTooltip.hidden = true;
  }

  private renderAbilityTooltip(): void {
    const tooltip = this.abilityTooltip;
    const player = this.lastPlayer;
    const slot = this.tooltipSlot;
    if (!tooltip || !player || !slot) return;
    const signature = `${player.heroId}:${slot}:${player.level}:${slot === 'attack' ? 0 : player.ranks[slot]}:${player.items.join(',')}`;
    if (signature !== this.tooltipSignature) {
      this.tooltipSignature = signature;
      const data = abilityTooltipData(player, slot);
      const head = document.createElement('div');
      head.className = 'tt-head';
      const title = document.createElement('b');
      title.textContent = data.title;
      const subtitle = document.createElement('span');
      subtitle.className = 'tt-sub';
      subtitle.textContent = data.subtitle;
      head.append(title, subtitle);

      const description = document.createElement('p');
      description.className = 'tt-desc';
      description.textContent = data.description;

      const list = document.createElement('dl');
      list.className = 'tt-stats';
      for (const stat of data.stats) {
        const row = document.createElement('div');
        row.className = 'tt-row';
        const label = document.createElement('dt');
        label.textContent = stat.label;
        const value = document.createElement('dd');
        value.textContent = stat.value;
        if (stat.detail) {
          const detail = document.createElement('small');
          detail.textContent = stat.detail;
          value.appendChild(detail);
        }
        row.append(label, value);
        list.appendChild(row);
      }

      tooltip.replaceChildren(head, description, list);
      if (data.note) {
        const note = document.createElement('p');
        note.className = 'tt-note';
        note.textContent = data.note;
        tooltip.appendChild(note);
      }
    }
    this.positionAbilityTooltip();
  }

  private positionAbilityTooltip(): void {
    const tooltip = this.abilityTooltip;
    const anchor = this.tooltipAnchor;
    if (!tooltip || !anchor || tooltip.hidden) return;
    const rect = anchor.getBoundingClientRect();
    const width = tooltip.offsetWidth;
    const height = tooltip.offsetHeight;
    let left = rect.left + rect.width / 2;
    left = Math.max(8 + width / 2, Math.min(window.innerWidth - 8 - width / 2, left));
    let top = rect.top - 10;
    const below = top - height < 8;
    if (below) top = Math.max(8, Math.min(window.innerHeight - height - 8, rect.bottom + 10));
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
    tooltip.classList.toggle('below', below);
  }

  update(sim: Simulation, aim: { x: number; z: number }, fps: number, mouseX: number, mouseY: number, selectedId = 0): void {
    const player = sim.player;
    if (!player) return;

    this.lastPlayer = player;
    this.setHero(player.heroId);

    const def = getHeroDef(player.heroId);
    this.playerName.textContent = def.displayName;
    if (this.portrait.firstChild?.nodeType !== Node.TEXT_NODE) {
      this.portrait.insertBefore(document.createTextNode(''), this.portrait.firstChild);
    }
    if (this.portraitHero !== player.heroId) {
      this.portraitHero = player.heroId;
      const url = this.portraits.get(player.heroId);
      this.portrait.style.backgroundImage = url ? `url(${url}), linear-gradient(160deg, #2b5f9e, #0d1d30)` : '';
      this.portrait.classList.toggle('has-image', Boolean(url));
      this.portrait.title = def.displayName;
      // sin retrato (falló el render) queda la inicial
      (this.portrait.firstChild as Text).data = url ? '' : def.displayName[0];
    }
    this.levelBadge.textContent = String(player.level);
    const xpRatio = player.level >= MAX_LEVEL ? 1 : player.xp / xpToNext(player.level);
    this.xpFill.style.width = `${Math.min(100, xpRatio * 100)}%`;
    if (player.alive) {
      const shield = player.shieldHp > 0 ? ` (+${Math.ceil(player.shieldHp)})` : '';
      this.hpText.textContent = `${Math.ceil(player.hp)} / ${player.maxHp}${shield}`;
      this.hpText.classList.remove('respawn');
    } else {
      const seconds = Math.max(0, Math.ceil((player.respawnAtTick - sim.tick) / TICK_RATE));
      this.hpText.textContent = t('Reapareces en {seconds}s', { seconds });
      this.hpText.classList.add('respawn');
    }
    if (this.gold) this.gold.textContent = t('{gold} de oro', { gold: player.gold });
    this.renderStats(sim, player, selectedId);
    this.renderInventory(player);
    if (this.boardOpen) this.renderHeroBoard(sim, player);
    if (this.recallBar && this.recallFill && this.recallText) {
      const recalling = player.alive && player.recallTicks > 0;
      this.recallBar.hidden = !recalling;
      if (recalling) {
        const left = Math.max(0, (RECALL_TICKS - player.recallTicks) / TICK_RATE);
        this.recallFill.style.width = `${Math.min(100, (player.recallTicks / RECALL_TICKS) * 100)}%`;
        this.recallText.textContent = t('Volviendo a base… {seconds} s', { seconds: left.toFixed(1) });
      }
    }
    const ratio = Math.max(0, player.hp / player.maxHp);
    this.hpFill.style.width = `${ratio * 100}%`;
    this.hpFill.style.background =
      ratio > 0.5
        ? 'linear-gradient(90deg, #2f8cff, #7bd4ff)'
        : ratio > 0.25
          ? 'linear-gradient(90deg, #ff9f43, #ffd166)'
          : 'linear-gradient(90deg, #ff4d4d, #ff8a5c)';

    this.fpsLabel.textContent = `${Math.round(fps)} FPS`;

    if (sim.match.enabled) {
      this.modeTag.textContent = this.modeLabel;
      this.scoreboard.hidden = false;
      this.scoreBlue.textContent = String(sim.match.score.blue);
      this.scoreRed.textContent = String(sim.match.score.red);
      if (sim.match.durationTicks > 0) {
        const remaining = Math.max(0, sim.match.durationTicks - sim.match.elapsedTicks);
        const overtime = sim.match.elapsedTicks > sim.match.durationTicks;
        this.timer.textContent = formatTicks(overtime ? sim.match.elapsedTicks - sim.match.durationTicks : remaining);
        this.timer.classList.toggle('overtime', overtime);
      } else {
        // sin límite de tiempo: el reloj cuenta el tiempo jugado
        this.timer.textContent = formatTicks(sim.match.elapsedTicks);
        this.timer.classList.remove('overtime');
      }
    } else {
      this.modeTag.textContent = this.modeLabel;
      this.scoreboard.hidden = true;
      this.timer.textContent = formatTicks(sim.tick);
      this.timer.classList.remove('overtime');
    }

    const aimHot = this.isAimingAtEnemy(sim, aim);
    if (aimHot !== this.aimHot) {
      this.aimHot = aimHot;
      this.gameCanvas.classList.toggle('aim-hot', aimHot);
    }

    for (const slot of SLOT_ORDER) {
      const element = this.slots.get(slot);
      if (!element) continue;
      const remaining = this.remainingCooldown(player, slot);
      const max = this.maxCooldown(player, slot);
      const fillRatio = max > 0 ? Math.max(0, Math.min(1, remaining / max)) : 0;
      element.fill.style.height = `${fillRatio * 100}%`;
      element.text.textContent = remaining > 0 ? (remaining / TICK_RATE).toFixed(1) : '';
      if (slot !== 'attack') {
        const rank = player.ranks[slot];
        const maxRanks = slot === 'r' ? 3 : 5;
        element.root.classList.toggle('locked', rank <= 0);
        if (element.pips && element.pips.childElementCount !== maxRanks) {
          element.pips.innerHTML = '<i></i>'.repeat(maxRanks);
        }
        element.pips?.querySelectorAll('i').forEach((pip, i) => pip.classList.toggle('on', i < rank));
        if (element.plus) element.plus.hidden = !canRankUp(player, slot);
        this.renderCharges(player, slot, element);
      }

      const previous = this.lastCooldown.get(slot) ?? 0;
      if (previous > 0 && remaining <= 0) {
        element.root.classList.remove('ready');
        void element.root.offsetWidth;
        element.root.classList.add('ready');
        window.setTimeout(() => element.root.classList.remove('ready'), 260);
      }
      this.lastCooldown.set(slot, remaining);
    }

    if (this.tooltipSlot) this.renderAbilityTooltip();
    if (!this.debug.hidden) this.renderDebug(sim, player, fps);
  }

  killFeed(killer: HeroState | undefined, victim: HeroState): void {
    const row = document.createElement('div');
    row.className = `feed-row ${killer?.team ?? ''}`;

    const killerName = document.createElement('b');
    killerName.textContent = killer ? heroDisplayName(killer.name) : t('Arena');
    const arrow = document.createElement('span');
    arrow.className = 'arrow';
    arrow.textContent = '→';
    const victimName = document.createElement('b');
    victimName.textContent = heroDisplayName(victim.name);

    row.append(killerName, arrow, victimName);
    this.killfeed.prepend(row);

    while (this.killfeed.children.length > 5) {
      this.killfeed.lastElementChild?.remove();
    }
    window.setTimeout(() => row.remove(), 4200);
  }

  private modeLabel = t('3V3 · TRES LÍNEAS');

  /** Etiqueta del modo de la partida (arriba, junto al marcador). */
  setModeLabel(label: string): void {
    this.modeLabel = label;
  }

  /**
   * Fin de partida: ¡Victoria! / Derrota desde el punto de vista del jugador
   * (`me`), el marcador y sus estadísticas (héroe, nivel, asesinatos, muertes,
   * duración).
   */
  showMatchEnd(
    winner: Team,
    blue: number,
    red: number,
    online = false,
    me?: { team: Team; heroId: HeroId; level: number; kills: number; deaths: number; seconds: number },
  ): void {
    const won = me ? me.team === winner : undefined;
    this.endTitle.textContent = won === undefined ? t('Fin de la partida') : won ? t('¡Victoria!') : t('Derrota');
    const card = this.matchEnd.querySelector('.end-card');
    card?.classList.toggle('victory', won === true);
    card?.classList.toggle('defeat', won === false);
    const sub = document.getElementById('end-sub');
    if (sub) sub.textContent = winner === 'blue' ? t('Ganó el equipo azul') : t('Ganó el equipo rojo');
    this.endScore.textContent = `${blue} : ${red}`;
    const stats = document.getElementById('end-stats');
    if (stats) {
      stats.replaceChildren();
      if (me) {
        const hero = document.createElement('div');
        hero.className = 'end-hero';
        const portrait = document.createElement('span');
        portrait.className = 'end-portrait';
        const url = this.portraits.get(me.heroId);
        if (url) portrait.style.backgroundImage = `url("${url}")`;
        const name = document.createElement('span');
        name.textContent = getHeroDef(me.heroId).displayName;
        hero.append(portrait, name);
        stats.append(hero);
        const minutes = Math.floor(me.seconds / 60);
        const seconds = String(Math.floor(me.seconds % 60)).padStart(2, '0');
        for (const [label, value] of [
          [t('Nivel'), String(me.level)],
          [t('Asesinatos'), String(me.kills)],
          [t('Muertes'), String(me.deaths)],
          [t('Duración'), `${minutes}:${seconds}`],
        ]) {
          const chip = document.createElement('div');
          chip.className = 'end-stat';
          const v = document.createElement('b');
          v.textContent = value;
          const l = document.createElement('span');
          l.textContent = label;
          chip.append(v, l);
          stats.append(chip);
        }
      }
    }
    this.matchEnd.hidden = false;

    const restart = document.getElementById('restart-btn') as HTMLButtonElement | null;
    if (restart) {
      restart.disabled = online;
      restart.textContent = online ? t('Volviendo a la sala…') : t('Volver al menú');
    }
  }

  hideMatchEnd(): void {
    this.matchEnd.hidden = true;
    const restart = document.getElementById('restart-btn') as HTMLButtonElement | null;
    if (restart) {
      restart.disabled = false;
      restart.textContent = t('Volver al menú');
    }
  }

  setNetStatus(text: string | null, state: 'idle' | 'connecting' | 'connected' | 'error' | 'closed' = 'idle'): void {
    if (!this.netStatus) return;
    this.netStatus.hidden = text === null;
    this.netStatus.textContent = text ?? '';
    this.netStatus.dataset.state = state;
  }

  /** Ping al servidor (online): ms de ida y vuelta, en verde/amarillo/rojo. `null` lo oculta. */
  setPing(ms: number | null): void {
    if (!this.pingEl) return;
    this.pingEl.hidden = ms === null;
    if (ms === null) return;
    const text = ms > 0 ? `${Math.round(ms)} ms` : '-- ms';
    if (text === this.lastPingText) return;
    this.lastPingText = text;
    const label = this.pingEl.querySelector('b');
    if (label) label.textContent = text;
    this.pingEl.dataset.quality = ms <= 0 ? 'unknown' : ms < 80 ? 'good' : ms < 150 ? 'fair' : 'bad';
  }

  /** Muestra el aviso de cámara libre cuando Y desbloquea la vista. */
  setCameraLocked(locked: boolean): void {
    if (!this.cameraTag) return;
    this.cameraTag.hidden = locked;
  }

  setDebug(visible: boolean): void {
    this.debug.hidden = !visible;
  }

  toggleDebug(): void {
    this.debug.hidden = !this.debug.hidden;
  }

  private remainingCooldown(player: HeroState, slot: HudSlot): number {
    if (slot === 'attack') return 0;
    return player.cooldowns[slot];
  }

  /**
   * Panel de estadísticas (arriba a la izquierda, bajo la vida): del propio héroe
   * o de la unidad inspeccionada con clic (héroe, minion, torre o nexo).
   */
  private renderStats(sim: Simulation, player: HeroState, selectedId: number): void {
    const panel = document.getElementById('hero-stats');
    if (!panel) return;
    const unit = selectedId ? sim.unitById(selectedId) : undefined;
    const shown = unit && unit.alive ? unit : player;
    const relation = shown === player ? t('Tú') : shown.team === player.team ? t('Aliado') : t('Enemigo');
    const pct = (v: number) => `${Math.round(v * 100)}%`;
    const perSecond = (ticks: number) => (TICK_RATE / Math.max(1, ticks)).toFixed(2);
    let kind: string;
    let title: string;
    let rows: Array<[key: string, label: string, value: string]>;

    if (isHeroUnit(shown)) {
      kind = 'hero';
      title = t('{hero} · nivel {level} · {relation}', { hero: getHeroDef(shown.heroId).displayName, level: shown.level, relation });
      rows = this.heroStatRows(shown, pct);
    } else if (isStructureUnit(shown)) {
      const stats = STRUCTURE_STATS[shown.kind];
      kind = 'structure';
      const name = t(shown.kind === 'nexus' ? 'Nexo' : shown.tier === 3 ? 'Torre del nexo' : shown.tier === 1 ? 'Torre exterior' : 'Torre interior');
      title = `${name} · ${relation}`;
      rows = [
        ['hp', 'Vida', `${Math.ceil(shown.hp)} / ${shown.maxHp}`],
        ['ad', 'Daño a héroes', String(stats.attack.damageVsHero)],
        ['dm', 'Daño a minions', String(stats.attack.damageVsMinion)],
        ['range', 'Alcance', stats.attack.range.toFixed(1)],
        ['as', 'Ataques/s', perSecond(stats.attack.cooldownTicks)],
        ['state', 'Estado', sim.isStructureVulnerable(shown) ? t('Atacable') : t('Protegida')],
      ];
    } else {
      const stats = MINION_STATS[shown.kind];
      kind = 'minion';
      title = `Koopa · ${relation}`;
      rows = [
        ['hp', 'Vida', `${Math.ceil(shown.hp)} / ${shown.maxHp}`],
        ['ad', 'Daño', String(stats.damage)],
        ['ds', 'Daño a estructuras', String(stats.structureDamage)],
        ['range', 'Alcance', stats.range.toFixed(1)],
        ['as', 'Ataques/s', perSecond(stats.cooldownTicks)],
        ['ms', 'Vel. movimiento', stats.speed.toFixed(1)],
      ];
    }

    // cambiar de tipo de unidad reconstruye las filas
    if (panel.dataset.kind !== kind) {
      panel.dataset.kind = kind;
      panel.innerHTML = '<div class="stat-title"></div>';
      this.statValues.clear();
    }
    const titleEl = panel.querySelector('.stat-title') as HTMLElement;
    if (titleEl.textContent !== title) titleEl.textContent = title;
    panel.classList.toggle('inspecting', shown !== player);
    for (const [key, label, value] of rows) {
      let el = this.statValues.get(key);
      if (!el) {
        const row = document.createElement('div');
        row.className = `stat ${key}`;
        row.innerHTML = '<span class="stat-label"></span><span class="stat-value"></span>';
        (row.querySelector('.stat-label') as HTMLElement).textContent = t(label);
        panel.appendChild(row);
        el = row.querySelector('.stat-value') as HTMLElement;
        this.statValues.set(key, el);
      }
      if (el.textContent !== value) el.textContent = value;
    }
  }

  private heroStatRows(hero: HeroState, pct: (v: number) => string): Array<[string, string, string]> {
    const stats = heroStats(hero.heroId, hero.level, hero.items);
    const baseRange = getHeroDef(hero.heroId).attacks[0].range;
    return [
      ['ad', 'Daño', String(Math.round(stats.attackDamage))],
      ['armor', 'Armadura', String(Math.round(stats.armor))],
      ['as', 'Vel. ataque', stats.attackSpeed.toFixed(2)],
      ['range', 'Alcance', (baseRange * stats.attackRange).toFixed(1)],
      ['haste', 'Acel. habilidad', String(Math.round(stats.abilityHaste))],
      ['ms', 'Vel. movimiento', stats.moveSpeed.toFixed(1)],
      ['regen', 'Regeneración', `${stats.hpRegen.toFixed(1)}/s`],
      ['vamp', 'Omnisucción', pct(stats.omnivamp)],
      ['leth', 'Letalidad', String(Math.round(stats.lethality))],
      ['pen', 'Pen. armadura', pct(stats.armorPen)],
      ['ten', 'Tenacidad', pct(stats.tenacity)],
      ['hp', 'Vida', `${Math.ceil(hero.hp)} / ${hero.maxHp}`],
    ];
  }

  /** Inventario de 6 casillas junto a la barra de habilidades. */
  private renderInventory(player: HeroState): void {
    const root = document.getElementById('inventory');
    if (!root) return;
    if (!this.inventorySlots.length) {
      for (let i = 0; i < 6; i++) {
        const slot = document.createElement('span');
        slot.className = 'inv-slot';
        root.appendChild(slot);
        this.inventorySlots.push(slot);
      }
    }
    this.inventorySlots.forEach((slot, i) => {
      const id = player.items[i] ?? '';
      if (slot.dataset.item === id) return;
      slot.dataset.item = id;
      if (id) itemIcon(id, slot);
      else {
        slot.textContent = '';
        slot.style.background = '';
        slot.title = '';
      }
    });
  }

  /** Tablero (O): héroes de la partida con su marcador y objetos. */
  private renderHeroBoard(sim: Simulation, player: HeroState): void {
    if (!this.boardTeams || !this.boardScore) return;
    this.boardScore.textContent = `${sim.match.score.blue} : ${sim.match.score.red}`;
    const signature = sim.heroes
      .map((hero) => `${hero.id}:${hero.team}:${hero.level}:${hero.kills}:${hero.deaths}:${hero.alive}:${hero.items.join(',')}`)
      .join('|');
    if (signature === this.boardSignature) return;
    this.boardSignature = signature;
    this.boardTeams.replaceChildren();
    for (const team of ['blue', 'red'] as const) {
      const column = document.createElement('div');
      column.className = `board-team ${team}`;
      const heading = document.createElement('div');
      heading.className = 'board-team-name';
      const kills = sim.heroes.filter((hero) => hero.team === team).reduce((sum, hero) => sum + hero.kills, 0);
      heading.textContent = team === 'blue' ? t('Equipo azul') : t('Equipo rojo');
      const total = document.createElement('span');
      total.textContent = t('{kills} asesinatos', { kills });
      heading.appendChild(total);
      column.appendChild(heading);
      const heroes = sim.heroes
        .filter((hero) => hero.team === team)
        .sort((a, b) => b.kills - a.kills || a.deaths - b.deaths || a.id - b.id);
      for (const hero of heroes) {
        const row = document.createElement('div');
        row.className = `board-row${hero.id === player.id ? ' me' : ''}${hero.alive ? '' : ' dead'}`;
        const portrait = document.createElement('span');
        portrait.className = 'board-portrait';
        const url = this.portraits.get(hero.heroId);
        if (url) portrait.style.backgroundImage = `url("${url}")`;
        const level = document.createElement('span');
        level.className = 'board-level';
        level.textContent = String(hero.level);
        level.title = t('Nivel {level}', { level: hero.level });
        portrait.appendChild(level);
        const name = document.createElement('div');
        name.className = 'board-name';
        const who = document.createElement('b');
        who.textContent = hero.id !== player.id || hero.name === 'You' ? heroDisplayName(hero.name) : t('{name} (tú)', { name: hero.name });
        const heroName = document.createElement('span');
        heroName.textContent = hero.alive ? getHeroDef(hero.heroId).displayName : t('{hero} · muerto', { hero: getHeroDef(hero.heroId).displayName });
        name.append(who, heroName);
        const score = document.createElement('div');
        score.className = 'board-score';
        score.textContent = `${hero.kills} / ${hero.deaths}`;
        score.title = t('Asesinatos / muertes');
        const items = document.createElement('div');
        items.className = 'board-items';
        for (let i = 0; i < SHOP.slots; i++) {
          const slot = document.createElement('span');
          slot.className = 'board-slot';
          const id = hero.items[i];
          if (id) itemIcon(id, slot);
          items.appendChild(slot);
        }
        row.append(portrait, name, score, items);
        column.appendChild(row);
      }
      this.boardTeams.appendChild(column);
    }
  }

  private maxCooldown(player: HeroState, slot: HudSlot): number {
    if (slot === 'attack') return 0;
    const base = abilityCooldown(getHeroDef(player.heroId).abilities[slot]);
    const haste = abilityHasteMultiplier(heroStats(player.heroId, player.level, player.items).abilityHaste);
    return Math.round(base * abilityCooldownScale(slot, player.ranks[slot]) * haste);
  }

  /** Habilidades con cargas: número restante y progreso de la siguiente. */
  private renderCharges(player: HeroState, slot: AbilitySlot, element: SlotElement): void {
    if (!element.charges || !element.chargeBar) return;
    const max = abilityCharges(getHeroDef(player.heroId).abilities[slot]);
    const show = max > 1 && player.ranks[slot] > 0;
    element.charges.hidden = !show;
    const recharging = show && player.chargeTicks[slot] > 0;
    element.chargeBar.hidden = !recharging;
    if (!show) return;
    const charges = player.charges[slot];
    element.charges.textContent = String(charges);
    element.charges.classList.toggle('empty', charges <= 0);
    if (recharging) {
      const total = Math.max(1, Math.round(this.maxCooldown(player, slot) / max));
      const progress = 1 - Math.min(1, player.chargeTicks[slot] / total);
      (element.chargeBar.firstElementChild as HTMLElement).style.width = `${progress * 100}%`;
    }
  }

  private isAimingAtEnemy(sim: Simulation, aim: { x: number; z: number }): boolean {
    for (const hero of sim.heroes) {
      if (!hero.alive || hero.isPlayer) continue;
      const dx = hero.pos.x - aim.x;
      const dz = hero.pos.z - aim.z;
      if (dx * dx + dz * dz < (hero.radius + 0.55) ** 2) return true;
    }
    return false;
  }

  private renderDebug(sim: Simulation, player: HeroState, fps: number): void {
    const action = player.action;
    const cooldowns = player.cooldowns;
    const lines = [
      `tick ${sim.tick}  |  ${(sim.tick / TICK_RATE).toFixed(1)}s  |  ${Math.round(fps)} fps`,
      `pos ${player.pos.x.toFixed(2)}, ${player.pos.z.toFixed(2)}  facing ${player.facing.toFixed(2)}`,
      `action ${action.kind}${action.slot ? `:${action.slot}` : ''} ${action.ticks}/${action.duration} chain ${action.chain}`,
      `hp ${Math.ceil(player.hp)}/${player.maxHp}  hitstun ${player.hitstun}  invuln ${player.invuln}`,
      `cd q ${(cooldowns.q / TICK_RATE).toFixed(1)}  w ${(cooldowns.w / TICK_RATE).toFixed(1)}  e ${(cooldowns.e / TICK_RATE).toFixed(1)}  r ${(cooldowns.r / TICK_RATE).toFixed(1)}`,
      `heroes ${sim.heroes.length}  projectiles ${sim.projectiles.length}`,
      `kills ${player.kills}  deaths ${player.deaths}  score ${sim.match.score.blue}:${sim.match.score.red}`,
    ];
    this.debug.textContent = lines.join('\n');
  }
}

function formatTicks(ticks: number): string {
  const seconds = Math.max(0, Math.floor(ticks / TICK_RATE));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}
